import { filter, forEach, assign } from 'lodash'
import React, { useEffect } from 'react';
import * as actions from '../actions';
import * as contractorActions from '../contractorActions';
import * as jobActions from '../jobActions';
import componentServices from '../componentServices';
import enums from '../enum';
import util from '../util';
import validator from '../validator';
import jobFactory, { IJob } from '../models/jobModel';
import JobCostEdit from './jobCostEdit';
import JobEditOverview from './jobEditOverview';
import JobEditFiles from './jobEditFiles';
import JobEditOperations from './jobEditOperations';
import JobEditPhotos from './jobEditPhotos';
import JobEstimatesList from './jobEstimatesList';
import JobPaymentsList from './jobPaymentsList';
import JobContractsList from './jobContractsList';
import { getCollection, getModel, ModelCollection, StoreInstance } from '../store';
import { Iterable } from 'immutable';
import { IJobCost } from '../models/jobCostModel';
import { useSaveRoot } from '../saveRoot';
import { IPatchOperation } from '../modelFactory';
import { ISiteVisit, SiteVisitStatus, SiteVisitType } from 'Scripts/models/siteVisitModel';
import { JobEditService } from './jobEditService';

function JobEditHeader() {
	const { routeParams, store } = componentServices;
	const moduleKey = routeParams.modulekey;
	const jobKey = routeParams.jobkey;
	const job = getModel({ store, collection: ModelCollection.jobs, key: jobKey})
	let jobCosts:Iterable<string, IJobCost> = getCollection({ store, collection: ModelCollection.jobCosts });
	let siteVisits:Iterable<string, ISiteVisit> = getCollection({ store, collection: ModelCollection.siteVisits });

	if(!job) {
		return null;
	}

	function handleRouteClick (key, e) {
		e.preventDefault();
		componentServices.router.pushHistory(componentServices.router.replacePathSegmentAt(3, key, true));
	}

	if (jobCosts && jobCosts.size) {
		jobCosts = jobCosts.filter(jc => jc.jobKey === jobKey);
	}

	if (siteVisits && siteVisits.size) {
		siteVisits = siteVisits.filter(v => v.jobKey === jobKey);
	}

	const items:JSX.Element[] = [];
	const buttons = [{
		path: 'client',
		name: 'Job Overview'
	}, {
		path: 'estimates',
		name: 'Estimates',
		test: () => ['retail-sales', 'builder-sales'].indexOf(moduleKey) !== -1
	}, {
		path: 'contracts',
		name: 'Contracts',
		test: () => jobCosts.some(jc => jc.isContract)
	}, {
		path: 'payment',
		name: 'Payments',
		test: j => j.businessSegment === enums.businessSegments.Retail
	}, {
		path: 'service',
		name: 'Service',
		test: () => siteVisits.some(v => v.currentStatus === SiteVisitStatus.completed && [SiteVisitType.delivery, SiteVisitType.install].indexOf(v.type) !== -1) && ['operations'].indexOf(moduleKey) !== -1
	}, {
		path: 'operations',
		name: 'Operations',
		test: () => jobCosts.some(jc => jc.isContract) && ['operations'].indexOf(moduleKey) !== -1
	}, {
		path: 'files',
		name: 'Files'
	}, {
		path: 'photos',
		name: 'Photos'
	}];

	forEach(filter(buttons, b => !b.test || b.test(job)), x => {
		let className = 'button';

		// TODO: deal with possible disabled status of header links
		className += componentServices.router.isActive(new RegExp(`${job.key}\/${x.path}$`)) ? ' on' : '';

		items.push(<li key={x.path}><a onClick={handleRouteClick.bind(null, x.path)} className={className}><span>{x.name}</span></a></li>);
	});

	const primaryContactName = (job && job.primaryClientContact) ? job.primaryClientContact.fullName : '';
	let primaryContactMethod = '';
	if (job && job.primaryClientContact) {
		const contact = job.primaryClientContact;
		switch (contact.preferredContactMethod) {
		case enums.contactMethods.Email:
			primaryContactMethod = contact.emailAddress;
			break;
		case enums.contactMethods.Home_Phone:
			primaryContactMethod = contact.homePhoneNumber;
			break;
		case enums.contactMethods.Office_Phone:
			primaryContactMethod = contact.workPhoneNumber;
			break;
		case enums.contactMethods.Mobile_Phone:
			primaryContactMethod = contact.mobilePhoneNumber;
			break;
		default:
			break;
		}
	}

	return (
		<header key={'header-' + job.key}>
			<div className={ 'jobStatus ' + job.currentStatus }>{ job.currentStatus } <span>{ util.dateHelper.formatDate(job.currentStatusItem.dateCreated, { userFriendly: true }) }</span></div>
			<div className="clientInfo">{primaryContactName} <span>{primaryContactMethod}</span></div>
			<ul className="menu">
				{items}
			</ul>
		</header>
	);
}

export interface IJobEditChildProps {
	job?: IJob;
	updateModel?: (updater: (job: IJob) => void, options: Record<string, any>) => void;
	store?: StoreInstance;
}

const JobEdit = ({ children, ...childProps } ) => {
	const { routeParams, store } = componentServices;
	const jobKey = routeParams.jobkey;
	const headerKey = 'job-edit-header';
	const [saveRoot] = useSaveRoot<IJob>({
		autoSave:  true,
		globalSave: true,
		warnOnLeave: true,
		stateId: `JobEdit-${jobKey}`,
		modelFactory: jobFactory,
		resolveMutableModel (current: IJob | null) {
			const { store } = componentServices;
			const storeJob = getModel({ store, collection: ModelCollection.jobs, key: jobKey });
			const builder = (storeJob && storeJob.builderKey) ? getModel({ store, collection: ModelCollection.contractors, key: storeJob.builderKey }): undefined;
			let needsFetch = !storeJob || storeJob.isListShape;
			let job: IJob = current;
			let rebase = false;

			if (storeJob) {
				if (!current || storeJob.key !== current.key) {
					job = jobFactory(storeJob.toDocument());

					// if this is a new job key, be sure to fetch the job for safety sake
					needsFetch = true;
				}
				else if (storeJob.key === current.key && (storeJob.itemVersion !== current.itemVersion || (current.isListShape && !storeJob.isListShape))) {
					job = jobFactory(storeJob.toDocument());
					rebase = true;
				}
			}

			if (storeJob && storeJob.builderKey && !builder) {
				// be sure we've fetched this builder
				contractorActions.fetchContractor({ key: storeJob.builderKey });
			}

			if (needsFetch) {
				jobActions.fetchJob({ key: jobKey });
			}

			return [ job, rebase ]
		},
		save: (doc, patch: IPatchOperation[]) => jobActions.saveJob({ job: doc, patch }),
		processAlert(alert) {
			alert.message = alert.message.replace('Client Contacts 1', 'Primary Client Contact');
			return alert;
		},
		validate(model) {
			const rules = assign({}, jobFactory.rules());
			const { store } = componentServices;
			const jobCosts = getCollection({ store, collection: ModelCollection.jobCosts}).filter(jc => jc.jobKey === model.key);

			if (jobCosts.count() > 0) {
				rules.billingAddress = rules.workSiteAddress = [validator.required()];
			}
			else {
				delete rules.billingAddress;
				delete rules.workSiteAddress;
			}

			return validator.test(model, rules, []);
		}
	}, getModel({ store, collection: ModelCollection.jobs, key: jobKey }));

	// manage header on dismount
	useEffect(() => {
		const { routeParams } = componentServices;
		const jobKey = routeParams.jobkey;

		actions.setHeader({
			key: headerKey,
			element: (
				<JobEditHeader key={ jobKey } />
			)
		});

		return () => {
			actions.deleteHeader({ key: headerKey });
		}
	}, []);

	function updateModel (updater, options) {
		saveRoot.update((job) => {
			updater(job);

			if (job.primaryClientContact && job.primaryClientContact.isEmpty()) {
				job.primaryClientContact = undefined;
			}
			if (job.secondaryClientContact && job.secondaryClientContact.isEmpty()) {
				job.secondaryClientContact = undefined;
			}
			if (job.billingAddress && job.billingAddress.isEmpty()) {
				job.billingAddress = undefined;
			}
			if (job.workSiteAddress && job.workSiteAddress.isEmpty()) {
				job.workSiteAddress = undefined;
			}

			if (componentServices.securityContext.currentUserHasFeature('retail-sales', 'manage-leads')) {
				// sets or clears the lead assignment setting based on the value of the accountManagerKey
				job.setSetting(enums.jobSettingGroups.Marketing, 'leadAssigned', job.accountManagerKey || undefined);
			}
		}, options);
	}

	return (saveRoot.model) ? children && React.cloneElement(children, assign({ job: saveRoot.model, updateModel }, childProps)) : null;
}

export const JobHeading = () => {
	const { routeParams, store } = componentServices;
	const jobKey = routeParams.jobkey;
	const storeJob = jobKey && getModel({ store, collection: ModelCollection.jobs, key: jobKey });

	return storeJob && storeJob.workSiteAddress ? <h1>{ enums.businessSegments.getName(storeJob.businessSegment)} • <span>{ storeJob.workSiteAddress.street }</span></h1> : null;
};

componentServices.router.addRoutes([{
	path: '/:modulekey/jobs/:jobkey/client',
	component: (props) => <ModuleWrapper><JobEdit { ...props }><JobEditOverview /></JobEdit></ModuleWrapper>,
	heading: <JobHeading />,
	contentClass: 'jobDetail',
	onEnter: componentServices.router.requireUser(({ securityContext, next }) => securityContext.getCurrentUser().hasModule(next.params.modulekey))
}, {
	path: '/:modulekey/jobs/:jobkey/files',
	component: (props) => <ModuleWrapper><JobEdit { ...props }><JobEditFiles title = "Files"/></JobEdit></ModuleWrapper>,
	heading: <JobHeading />,
	contentClass: 'jobDetail docs',
	onEnter: componentServices.router.requireUser(({ securityContext, next }) => securityContext.getCurrentUser().hasModule(next.params.modulekey))
}, {
	path: '/:modulekey/jobs/:jobkey/photos',
	component: (props) => <ModuleWrapper><JobEdit { ...props }><JobEditPhotos /></JobEdit></ModuleWrapper>,
	heading: <JobHeading />,
	contentClass: 'jobDetail',
	onEnter: componentServices.router.requireUser(({ securityContext, next }) => securityContext.getCurrentUser().hasModule(next.params.modulekey))
}, {
	path: '/:modulekey/jobs/:jobkey/estimates/:jobcostkey/:viewkey?',
	component: (props) => <ModuleWrapper><JobEdit { ...props }><JobCostEdit /></JobEdit></ModuleWrapper>,
	heading: <JobHeading />,
	contentClass: 'jobDetail',
	onEnter: ({ next, replace }) => {
		const accessTest = componentServices.router.requireUser(({ securityContext }) => securityContext.getCurrentUser().hasModule(next.params.modulekey));
		if (accessTest({ next, replace }) !== false && !next.params.viewkey) {
			replace(next.location.pathname + '/setup');
			return false; // be sure not to render this route
		}
	}
}, {
	path: '/:modulekey/jobs/:jobkey/estimates',
	component: (props) => <ModuleWrapper><JobEdit { ...props }><JobEstimatesList /></JobEdit></ModuleWrapper>,
	heading: <JobHeading />,
	contentClass: 'jobDetail',
	onEnter: componentServices.router.requireUser(({ securityContext, next }) => securityContext.getCurrentUser().hasModule(next.params.modulekey))
}, {
	path: '/:modulekey/jobs/:jobkey/payment',
	component: (props) => <ModuleWrapper><JobEdit { ...props }><JobPaymentsList /></JobEdit></ModuleWrapper>,
	heading: <JobHeading />,
	contentClass: 'jobDetail',
	onEnter: componentServices.router.requireUser(({ securityContext, next }) => securityContext.getCurrentUser().hasModule(next.params.modulekey))
}, {
	path: '/:modulekey/jobs/:jobkey/contracts/:jobcostkey/:variable*',
	component: (props) => <ModuleWrapper><JobEdit { ...props }><JobCostEdit /></JobEdit></ModuleWrapper>,
	heading: <JobHeading />,
	contentClass: 'jobDetail',
	onEnter: ({ next, replace }) => {
		const accessTest = componentServices.router.requireUser(({ securityContext }) => securityContext.getCurrentUser().hasModule(next.params.modulekey));
		if (accessTest({ next: next, replace: replace }) !== false) {
			if (next.params.variable) {
				const parts = next.params.variable.split('/');
				if (parts.length === 1) {
					if(parts[0] === 'composite') {
						next.params.viewmode = 'composite';
					}
					else {
						next.params.viewkey = parts[0];
					}
				}
				else if (parts.length === 2) {
					if(parts[0] === 'composite') {
						next.params.viewmode = 'composite';
						next.params.viewkey = parts[1];
					}
					else {
						next.params.changeordertype = parts[0];
						next.params.changeorderkey = parts[1];
					}
				}
				else if (parts.length === 3) {
					next.params.changeordertype = parts[0];
					next.params.changeorderkey = parts[1];
					next.params.viewkey = parts[2];
				}
			}

			if (!next.params.viewkey) {
				replace(next.location.pathname + '/setup');
				return false; // be sure not to render this route
			}
		}
	}
}, {
	path: '/:modulekey/jobs/:jobkey/contracts',
	component: (props) => <ModuleWrapper><JobEdit { ...props }><JobContractsList /></JobEdit></ModuleWrapper>,
	heading: <JobHeading />,
	contentClass: 'jobDetail jobContract',
	onEnter: componentServices.router.requireUser(({ securityContext, next }) => securityContext.getCurrentUser().hasModule(next.params.modulekey))
}, {
	path: '/:modulekey/jobs/:jobkey/service',
	component: (props) => <ModuleWrapper><JobEdit { ...props }><JobEditService /></JobEdit></ModuleWrapper>,
	heading: <JobHeading />,
	contentClass: 'jobDetail jobService',
	onEnter: componentServices.router.requireUser(({ securityContext, next }) => securityContext.getCurrentUser().hasModule(next.params.modulekey))
}, {
	path: '/:modulekey/jobs/:jobkey/operations',
	component: (props) => <ModuleWrapper><JobEdit { ...props }><JobEditOperations /></JobEdit></ModuleWrapper>,
	heading: <JobHeading />,
	contentClass: 'jobDetail jobOperations',
	onEnter: componentServices.router.requireUser(({ securityContext, next }) => securityContext.getCurrentUser().hasModule(next.params.modulekey))
}, {
	path: '/:modulekey/jobs/:jobkey',
	onEnter: ({ next, replace }) => {
		replace(`/${next.params.modulekey}/jobs/${next.params.jobkey}/client`);
		// cancel the route transition
		return false;
	}
}]);

export default JobEdit;

function ModuleWrapper({ children }) {
	// const moduleKey = componentServices.routeParams?.modulekey;

	return children;
}